import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CardSkeleton, Icon, PageHeader, Tabs } from '../components/ui';
import { RANGES, rangeFor } from '../observability/contract';
import {
  loadAlarms, loadDatabase, loadDeployments, loadEdge, loadJobs, loadKubernetes, loadOverview,
} from '../observability/client';
import usePlatformData from '../observability/usePlatformData';
import OverviewPanel from '../components/platform/OverviewPanel';
import ServicesPanel from '../components/platform/ServicesPanel';
import ClusterPanel from '../components/platform/ClusterPanel';
import DatabasePanel from '../components/platform/DatabasePanel';
import EdgePanel from '../components/platform/EdgePanel';
import JobsPanel from '../components/platform/JobsPanel';
import DeploymentsPanel from '../components/platform/DeploymentsPanel';
import AlarmsPanel from '../components/platform/AlarmsPanel';
import SourceWarnings from '../components/platform/SourceWarnings';

/* ── Platform: the state of the infrastructure, without opening the AWS console ──
   The Dashboard answers "what are users doing and what does it cost"; this page
   answers "is the platform healthy, and where is the pressure". Everything is read
   live through /api/observability (see observability/contract.js).

   State lives in the query string (?view=services&service=proxy-llm&range=24h) so a
   link to one service's charts can be pasted into a ticket or a chat. That is also
   why this page has no deep route: a new route would need its own `need`, and the
   sidebar entry already carries one. */

const VIEWS = [
  { value: 'overview', label: 'Overview', icon: 'dashboard' },
  { value: 'services', label: 'Services', icon: 'server' },
  { value: 'cluster', label: 'Cluster', icon: 'infrastructure' },
  { value: 'database', label: 'Database', icon: 'database' },
  { value: 'network', label: 'Network and storage', icon: 'globe' },
  { value: 'jobs', label: 'Jobs', icon: 'clock' },
  { value: 'deployments', label: 'Deployments', icon: 'cloudUpload' },
  { value: 'alarms', label: 'Alarms', icon: 'alarm' },
];

// Views whose data comes from their own endpoint. Overview and Services read the
// overview payload (Services then fetches per-service metrics itself).
const PANEL_LOADERS = {
  cluster: loadKubernetes,
  database: loadDatabase,
  network: loadEdge,
  jobs: () => loadJobs(),
  deployments: () => loadDeployments(),
  alarms: () => loadAlarms(),
};

const refreshText = s => (s % 60 ? `every ${s} s` : s === 60 ? 'every minute' : `every ${s / 60} min`);

function StaleNotice({ error, loadedAt, onRetry }) {
  return (
    <div role="status" className="notice notice-warn">
      <div className="notice-body">
        <strong>Showing older data.</strong> The last refresh failed ({error}).
        {loadedAt && ` These figures are from ${loadedAt.toLocaleTimeString()}.`}
      </div>
      <button type="button" className="btn btn-secondary btn-sm" onClick={onRetry}>Retry</button>
    </div>
  );
}

function LoadFailed({ what, error, onRetry }) {
  return (
    <div className="card load-failed" role="alert">
      <Icon name="alertCircle" size={20} />
      <div className="grow">
        <h2 className="m-0 text-md">Could not load {what}</h2>
        <p className="text-muted mt-1 mb-0">{error}</p>
      </div>
      <button type="button" className="btn btn-primary btn-sm" onClick={onRetry}>Try again</button>
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div className="dashboard-grid" role="status" aria-busy="true">
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="dashboard-tile-6"><CardSkeleton lines={5} /></div>
      ))}
    </div>
  );
}

function Platform() {
  const [params, setParams] = useSearchParams();
  const view = VIEWS.some(v => v.value === params.get('view')) ? params.get('view') : 'overview';
  const range = rangeFor(params.get('range')).value;
  const service = params.get('service') || '';

  const update = useCallback((patch) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v == null || v === '' || (k === 'view' && v === 'overview') || (k === 'range' && v === '24h')) {
          next.delete(k);
        } else {
          next.set(k, v);
        }
      });
      return next;
    }, { replace: true });
  }, [setParams]);

  const refreshS = rangeFor(range).refresh;
  const poll = { intervalMs: refreshS * 1000 };
  const overview = usePlatformData(() => loadOverview(range), [range], poll);
  const loader = PANEL_LOADERS[view];
  const panel = usePlatformData(() => (loader ? loader(range) : Promise.resolve(null)), [view, range], poll);

  const reloadOverview = overview.reload;
  const reloadPanel = panel.reload;
  const refresh = useCallback(() => { reloadOverview(); reloadPanel(); }, [reloadOverview, reloadPanel]);

  const ov = overview.data;
  const services = useMemo(() => ov?.services || [], [ov]);
  const firing = (ov?.alarms?.items || []).filter(a => a.state === 'ALARM').length;
  const tabs = VIEWS.map(v => (v.value === 'alarms' && firing ? { ...v, count: firing } : v));
  const openService = useCallback((id) => update({ view: 'services', service: id }), [update]);
  const env = ov?.env;
  // The overview's own warnings on the two views drawn from it; the panel's on the rest.
  const warnings = (view === 'overview' || view === 'services' ? ov?.warnings : panel.data?.warnings) || [];

  const header = (
    <PageHeader
      description="Health, capacity and resource use of every service behind the platform: proxies, bridges, the cluster, the database and the network in front of them."
      meta={env && (
        <div className="page-meta">
          <span className="row gap-1"><Icon name="cloud" size={14} />{env.name} · {env.region}</span>
          <span className="mono">{env.cluster}</span>
          {overview.loadedAt && <span>Updated {overview.loadedAt.toLocaleTimeString()}</span>}
          <span>Refreshes {refreshText(refreshS)}</span>
        </div>
      )}
      actions={(
        <div className="row row-wrap gap-2">
          <Tabs size="sm" label="Time range" value={range} onChange={v => update({ range: v })}
                tabs={RANGES.map(r => ({ value: r.value, label: r.label }))} />
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh}>
            <Icon name="refresh" />
            Refresh
          </button>
        </div>
      )}
    >
      <h1 className="page-title">Platform</h1>
    </PageHeader>
  );

  let body;
  if (!ov && overview.error) {
    body = <LoadFailed what="the platform overview" error={overview.error} onRetry={overview.reload} />;
  } else if (!ov) {
    body = <PanelSkeleton />;
  } else if (view === 'overview') {
    body = <OverviewPanel overview={ov} onOpenService={openService} />;
  } else if (view === 'services') {
    body = (
      <ServicesPanel services={services} selected={service} range={range} refreshMs={refreshS * 1000}
                     onSelect={id => update({ service: id })} />
    );
  } else if (!panel.data && panel.error) {
    body = <LoadFailed what={VIEWS.find(v => v.value === view).label.toLowerCase()} error={panel.error} onRetry={panel.reload} />;
  } else if (!panel.data) {
    body = <PanelSkeleton />;
  } else if (view === 'cluster') {
    body = <ClusterPanel data={panel.data} range={range} />;
  } else if (view === 'database') {
    body = <DatabasePanel data={panel.data} range={range} />;
  } else if (view === 'network') {
    body = <EdgePanel data={panel.data} range={range} />;
  } else if (view === 'jobs') {
    body = <JobsPanel data={panel.data} />;
  } else if (view === 'deployments') {
    body = <DeploymentsPanel data={panel.data} />;
  } else {
    body = <AlarmsPanel data={panel.data} onOpenService={openService} />;
  }

  const stale = (ov && overview.error) ? overview : (panel.data && panel.error ? panel : null);

  return (
    <div className="platform-page">
      {header}
      {stale && <StaleNotice error={stale.error} loadedAt={stale.loadedAt} onRetry={stale.reload} />}
      <SourceWarnings warnings={warnings} />
      <div className="platform-tabs">
        <Tabs variant="line" label="Platform views" idPrefix="platform" value={view}
              onChange={v => update({ view: v })} tabs={tabs} />
      </div>
      <div role="tabpanel" id={`platform-panel-${view}`} aria-labelledby={`platform-tab-${view}`}>
        {body}
      </div>
    </div>
  );
}

export default Platform;
